import { useCallback, useEffect, useState } from "react";
import { Trophy, Target, Coins, Info, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { UserAvatar } from "@/components/UserBits";

export const SKILL_UNIT = { memory: "Züge", reaction: "ms", puzzle: "Züge" };

export function useSkill() {
  const [ov, setOv] = useState(null);
  const load = useCallback(() => api.get("/skill/overview").then(({ data }) => setOv(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  return { ov, load };
}

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="lux-card rounded-2xl px-3 py-2.5 min-w-0" data-testid={testId}>
    <p className="flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider text-rose-400"><Icon className="w-3 h-3" /> {label}</p>
    <p className="font-display text-lg font-extrabold text-slate-800 tabular-nums truncate">{value}</p>
  </div>
);

export function SkillInfo({ game, g, rules }) {
  const unit = SKILL_UNIT[game];
  return (
    <div className="space-y-3" data-testid={`skill-info-${game}`}>
      <div className="grid grid-cols-3 gap-2">
        <Stat icon={Coins} label="Gewertet" value={`${g.plays_left}/${g.plays_per_day}`} testId={`skill-${game}-plays-left`} />
        <Stat icon={Trophy} label="Bestwert" value={g.best != null ? `${g.best} ${unit}` : "–"} testId={`skill-${game}-best`} />
        <Stat icon={Target} label="Top-Prämie" value={`+${g.tiers[0].points}`} testId={`skill-${game}-max`} />
      </div>
      <details className="lux-card rounded-2xl p-4 group" data-testid={`skill-${game}-rules`}>
        <summary className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer list-none"><Info className="w-4 h-4 text-rose-500" /> Spielregeln & Punkte</summary>
        <ul className="mt-3 space-y-1.5 text-sm text-slate-600 list-disc pl-5">{rules.map((r) => <li key={r}>{r}</li>)}</ul>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {g.tiers.map((t) => <span key={t.max} className="px-2.5 h-7 inline-flex items-center rounded-full bg-rose-50 text-rose-700 text-xs font-bold">≤ {t.max} {unit}: +{t.points}</span>)}
          <span className="px-2.5 h-7 inline-flex items-center rounded-full bg-slate-100 text-slate-600 text-xs font-bold">sonst +{g.fallback}</span>
        </div>
        <p className="mt-3 text-xs text-slate-500">Pro Tag werden {g.plays_per_day} {g.plays_per_day === 1 ? "Runde" : "Runden"} mit Punkten gewertet. Danach kannst du weiter üben und deinen Bestwert verbessern.</p>
      </details>
    </div>
  );
}

export function SkillTop({ game, g }) {
  if (!g.top?.length) return null;
  return (
    <section className="lux-card rounded-3xl p-4" data-testid={`skill-${game}-top`}>
      <h3 className="flex items-center gap-2 font-display font-extrabold text-slate-800"><Trophy className="w-4 h-4 text-amber-500" /> Die Besten heute</h3>
      <ol className="mt-3 space-y-2">
        {g.top.map((u, i) => (
          <li key={u.user_id} className="flex items-center gap-3" data-testid={`skill-${game}-top-${i}`}>
            <span className={`w-6 text-center font-extrabold ${i === 0 ? "text-amber-500" : "text-slate-400"}`}>{i + 1}</span>
            <UserAvatar name={u.name} picture={u.picture} userId={u.user_id} size="w-8 h-8" noStory />
            <span className="flex-1 min-w-0 truncate text-sm font-semibold">{u.name}</span>
            <span className="text-sm font-extrabold tabular-nums text-rose-600">{u.score} {SKILL_UNIT[game]}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function SkillResult({ game, result, onAgain }) {
  return (
    <div className="lux-card rounded-3xl p-6 text-center stagger" data-testid={`skill-${game}-result`}>
      <p className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-rose-500">{result.record ? "Neuer Bestwert!" : "Geschafft!"}</p>
      <p className="mt-1 font-display text-4xl font-black text-slate-900 tabular-nums" data-testid={`skill-${game}-result-score`}>{result.score} <span className="text-lg text-slate-500">{SKILL_UNIT[game]}</span></p>
      {result.points > 0
        ? <p className="mt-3 inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-gradient-to-r from-amber-200 to-amber-400 text-amber-950 font-extrabold glow-gold" data-testid={`skill-${game}-result-points`}><Coins className="w-4 h-4" /> +{result.points} Punkte</p>
        : <p className="mt-3 text-sm text-slate-500" data-testid={`skill-${game}-result-points`}>Diese Runde zählt als Übung – für heute sind alle gewerteten Runden gespielt.</p>}
      <p className="mt-2 text-xs text-slate-500">Bestwert: {result.best} {SKILL_UNIT[game]} · Heute noch {result.plays_left} gewertet</p>
      <button type="button" onClick={onAgain} data-testid={`skill-${game}-again`} className="tap mt-5 inline-flex items-center gap-2 h-12 px-6 rounded-full pink-gradient text-white font-extrabold pink-glow"><RotateCcw className="w-4 h-4" /> Nochmal spielen</button>
    </div>
  );
}
